import { Platform } from '@angular/cdk/platform';
import { AnalyticsService } from '@shared/services/google-tag-manager/analytics/analytics.service';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
  ViewContainerRef,
  Inject,
  Optional,
  ComponentRef,
  AfterViewInit,
  Injector,
  afterNextRender,
  signal,
  TemplateRef,
} from '@angular/core';
import { StandService } from 'app/stand/stand.service';
import { ActivatedRoute, Router } from '@angular/router';
import { map, tap, take, concatMap, catchError } from 'rxjs/operators';
import { Observable, Subject, merge, of } from 'rxjs';
import { IProduct } from '@collections/products/interfaces';
import { LanguagesService, Language } from '@shared/services/languages/languages.service';
import { RoutesService } from '@shared/services/routes/route.service';
import { IPathLocale } from '@collections/common/interfaces/path-locale.interface';
import { ProductBySlugGQL } from '@shared/services/products/graphql/product-by-slug.gql.service';
import { ICompany } from '@collections/companies/interfaces/company.interface';
import { MetadataService } from '@shared/services/meta-data-service/meta-data.service';
import { ProductsComponent } from '../products/products.component';
import { DomSanitizer } from '@angular/platform-browser';
import { UpdateViewsProductCounterGQL } from 'app/shared/services/products/graphql/update-views-product-counter.gql.service';
import { ProductRatingComponent } from './product-rating/product-rating.component';
import { ResizeVideoUrlPipe } from '../../../shared/pipes/resize-video-url';
import { GetProductUrlPipe } from '../../../shared/pipes/get-product-url.pipe';
import { GetCompanyUrlPipe } from '../../../shared/pipes/get-company-url.pipe';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ProductRequestComponent as ProductRequestComponent_1 } from '../../components/product-request/product-request.component';
import { LanguageSelectorComponent } from '../../../shared/components/language-selector/language-selector.component';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ExpShareButtonsComponent } from '../../../shared/components/exp-share-buttons/exp-share-buttons.component';
import { RenderIfDirective } from '../../../shared/directives/render-if.directive';
import { IvyCarouselModule } from 'ngx-ivy-responsive-carousel';
import { CldPictureComponent } from '../../../shared/components/cld-picture/cld-picture.component';
import { HTMLContentComponent } from 'app/shared/components/html-content/html-content.component';
import { RobotsIndexEnum } from 'app/shared/services/meta-data-service/meta-data.commons';
import { ExpoButtonComponent } from 'app/shared/components/expo-button/expo-button.component';
import { UsersService } from 'app/shared/services/users/users.service';

@Component({
    selector: 'product-details',
    templateUrl: './product-details.component.html',
    styleUrls: ['./product-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CldPictureComponent, IvyCarouselModule, RenderIfDirective, ExpShareButtonsComponent, AvatarComponent, LanguageSelectorComponent, ProductRequestComponent_1, ProductRatingComponent, ProductsComponent, DatePipe, GetCompanyUrlPipe, GetProductUrlPipe, ResizeVideoUrlPipe, HTMLContentComponent, ExpoButtonComponent]
})
export class ProductDetailsComponent implements OnInit {
  product$: Observable<IProduct>;
  product = signal<IProduct>(null);
  mainImage: string;
  alternativeLangs: IPathLocale[] = [];
  company: ICompany;
  selectedLanguage: Language;
  isFallbackLanguage: boolean;
  availableLanguages: string[];
  itemListId: string;
  itemListName: string;

  constructor(
    private standService: StandService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private metadataService: MetadataService,
    private languagesService: LanguagesService,
    private productBySlugGQL: ProductBySlugGQL,
    private routesService: RoutesService,
    @Inject('FALLBACK_PRODUCT_IMAGE') public productFallbackImage,
    @Optional() private analyticsService: AnalyticsService,
    public platform: Platform,
    private sanitizer: DomSanitizer,
    private updateViewsProductCounterGQL: UpdateViewsProductCounterGQL,
    private usersService: UsersService
  ) {

    if(platform.isBrowser) {
      const state = history.state;
      this.itemListId = state?.itemListId;
      this.itemListName = state?.itemListName;
    }
    afterNextRender(() => {
      if (this.product) {
        this.updateViewsProductCounterGQL.mutate({
          id: this.product()._id
        }).pipe(take(1)).subscribe();
      }

    })
  }

  ngOnInit(): void {
    this.product$ = this.route.data
      .pipe(
        map((data) => data.product),
        tap(data => this.mainImage = data.images[0])
      );

    this.standService.getCurrentCompany()
      .pipe(
        take(1),
        concatMap(company => {
          this.company = company;
          return this.product$;
        }),
        catchError(_ => of(null))
      )
      .subscribe((product) => {
        if(!product) {
          return;
        }
        this.isFallbackLanguage = false;
        this.alternativeLangs = [];
        this.product.set(product);

        for (const alternativeLang of product.alternativeLangPath) {
          const companyPathLocale = this.company.primaryPathLocales.find(p => p.lang === alternativeLang.lang);
          const alternativeUrl = this.routesService.getProductUrl(this.company, alternativeLang.path, companyPathLocale.path);
          this.alternativeLangs.push({ lang: alternativeLang.lang, path: alternativeUrl });
        }
        this.availableLanguages = this.alternativeLangs.map(l => l.lang);
        // Insert Stand home url as base Path
        for (let lang of this.company.translations) {
          if(!this.availableLanguages.some(v => v === lang)) {
            this.alternativeLangs.push({ lang: lang, path: null, basePath: this.routesService.getCompanyAltLangUrl(this.company, lang) });
          }
        }

        let canonicalPath: IPathLocale;
        let productLang = this.languagesService.getCurrentLangCode();

        // determino se è presente una traduzione del prodotto nella lingua corrente dell'applicazione
        canonicalPath = this.alternativeLangs.find(translation => translation.lang === productLang);
        if (!canonicalPath.path) {
          this.isFallbackLanguage = true;
          productLang = 'en';
          // assegno il canonical url al linguaggio di default inglese
          canonicalPath = this.alternativeLangs.find(p => p.lang === productLang);
          // imposto linguaggio del language-selector
          this.selectedLanguage = this.languagesService.getLanguageByCode(productLang);
        }

        // const url = this.routesService.getProductUrl(canonicalPath.path, this.company.slug, product.slug);
        // canonicalPath = {lang: productLang, path: url} ;

        // aggiungo Meta tags
        this.metadataService.appendMetadata({
          title: product.name,
          description: product.shortDescription,
          images: product.images,
          pageType: 'product',
          company: this.company,
          categories: product.categories.map(c => c.refPath)
        },
          canonicalPath,
          !this.isFallbackLanguage ? this.alternativeLangs: null,
          RobotsIndexEnum.index_follow,
          product
        );

        if (this.platform.isBrowser) {
          this.analyticsService.ecTrackViewItem(this.product(), this.itemListId, this.itemListName);
        }

    });
  }

  changeImage(image): void {
    this.mainImage = image;
    this.cd.detectChanges();
  }

  fetchTranslation(language: Language): void {
    this.selectedLanguage = language;
    const lang = language.code;
    const slug = this.product().alternativeLangPath.find(a => a.lang === lang).path;

    this.product$ = merge(this.product$, this.productBySlugGQL.fetch(
      { companyId: this.company._id, lang: lang, slug: slug },
      { fetchPolicy: 'network-only' }
    )
      .pipe(
        take(1),
        map((result) => result.data.productBySlug)
      ));
  }

  isSavedProduct( ) {
    return (this.usersService.isSavedProduct(this.product()._id));
  }

  toggleSaveProduct( action: 'save'|'remove') {
    this.usersService.toggleSaveProduct(this.product(), action);
  }
}
